import React from "react";
import { supabase } from "../supabase";
import getSharedAudioContext from "./audioContext";
import { getBeatsPerMeasure } from "../utils/timing";

const normalizeCueKey = (cueKey = "") => cueKey.toLowerCase().trim();
const normalizeCountKey = (countKey = "") => countKey.toLowerCase().trim();
const GUIDE_TIMING_OFFSET_SEC = -0.05;
const COUNT_KEYS = ["one", "two", "three", "four", "five", "six"];
const getSectionCueKey = (section) => {
  const raw = section?.cue_key || section?.label || section?.section_label || "";
  const normalized = normalizeCueKey(raw);
  if (normalized === "none") {
    return null;
  }
  return normalized || null;
};

export function useGuidePlayer({ timeline, beatClock, isEnabled, startBeat = 0, beatOffset = 0, includeLeadIn = true, volume = 1, pan = 0 }) {
  const ctxRef = React.useRef(null);
  const buffersRef = React.useRef(new Map());
  const countBuffersRef = React.useRef(new Map());
  const sourcesRef = React.useRef([]);
  const masterGainRef = React.useRef(null);
  const panNodeRef = React.useRef(null);
  const playIdRef = React.useRef(0);
  const startBeatRef = React.useRef(startBeat);
  const debugEnabled = React.useCallback(() => {
    try {
      return window?.__ctDebugGuide === true || localStorage.getItem("ct-debug-guide") === "1";
    } catch (err) {
      return false;
    }
  }, []);

  const apiBase = import.meta.env.VITE_CUE_WORKER_URL || "";

  const ensureCtx = React.useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = getSharedAudioContext();
    }
    if (!masterGainRef.current || !panNodeRef.current) {
      const ctx = ctxRef.current;
      const gain = ctx.createGain();
      const panNode = ctx.createStereoPanner();
      gain.connect(panNode).connect(ctx.destination);
      masterGainRef.current = gain;
      panNodeRef.current = panNode;
    }
    return ctxRef.current;
  }, []);

  React.useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = volume;
    }
  }, [volume]);

  React.useEffect(() => {
    if (panNodeRef.current) {
      panNodeRef.current.pan.value = pan;
    }
  }, [pan]);

  const stopAll = React.useCallback(() => {
    sourcesRef.current.forEach((src) => {
      try {
        src.stop(0);
      } catch (_) {
        // ignore stop errors
      }
    });
    sourcesRef.current = [];
  }, []);

  const loadCueBuffer = React.useCallback(async (cueKey) => {
    const key = normalizeCueKey(cueKey);
    if (!key || key === "section" || buffersRef.current.has(key)) return;
    const ctx = ensureCtx();
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    const fileName = `${key}.m4a`;
    const url = `${apiBase}/api/cues?file=${encodeURIComponent(fileName)}`;
    let res;
    try {
      res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
    } catch (err) {
      return;
    }
    if (!res.ok) {
      return;
    }
    const contentType = res.headers.get("content-type");
    if (!contentType || !contentType.includes("audio")) {
      return;
    }
    const data = await res.arrayBuffer();
    let preview = null;
    try {
      preview = Array.from(new Uint8Array(data.slice(0, 12)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(" ");
    } catch (_) {
      preview = null;
    }
    try {
      const buffer = await ctx.decodeAudioData(data.slice(0));
      buffersRef.current.set(key, buffer);
    } catch (err) {
      return;
    }
  }, [ensureCtx]);

  const loadCountBuffer = React.useCallback(async (countKey) => {
    const key = normalizeCountKey(countKey);
    if (!key || countBuffersRef.current.has(key)) return;
    const ctx = ensureCtx();
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    const fileName = `${key}.m4a`;
    const url = `${apiBase}/api/counts?file=${encodeURIComponent(fileName)}`;
    let res;
    try {
      res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
    } catch (err) {
      return;
    }
    if (!res.ok) {
      return;
    }
    const contentType = res.headers.get("content-type");
    if (!contentType || !contentType.includes("audio")) {
      return;
    }
    const data = await res.arrayBuffer();
    let buffer;
    try {
      buffer = await ctx.decodeAudioData(data.slice(0));
    } catch (err) {
      return;
    }
    countBuffersRef.current.set(key, buffer);
  }, [ensureCtx]);

  const ensureBuffers = React.useCallback(async () => {
    const sections = timeline?.sections || [];
    const cueKeys = Array.from(new Set(sections.map((s) => getSectionCueKey(s)).filter(Boolean)));
    if (!cueKeys.includes("ending")) {
      cueKeys.push("ending");
    }
    const countKeys = COUNT_KEYS;
    const results = await Promise.allSettled([
      ...cueKeys.map(loadCueBuffer),
      ...countKeys.map(loadCountBuffer),
    ]);
    const failed = results
      .map((res, idx) => ({ res, key: cueKeys[idx] }))
      .filter((item) => item.res.status === "rejected");
    if (failed.length) {
      failed.forEach((item) => {
      });
    }
  }, [loadCueBuffer, loadCountBuffer, timeline]);

  const scheduleCues = React.useCallback(
    async (baselineOverride = null) => {
      if (!timeline) {
        return;
      }
      if (timeline.ok === false) {
        return;
      }
      const sections = timeline.sections || [];
      if (!sections.length) {
        return;
      }
      if (!beatClock?.isAnchored?.()) {
        return;
      }

      const ctx = ensureCtx();
      try {
        await ctx.resume?.();
      } catch (_) {
        // ignore resume errors
      }

      await ensureBuffers();

      const beatsPerMeasure = getBeatsPerMeasure(
        beatClock?.beatsPerMeasure ?? timeline.beats_per_measure ?? timeline.beatsPerMeasure,
      );

      stopAll();

      const scheduleBufferAtBeat = (buffer, beatIndex) => {
        if (!buffer) return;
        const playTime = beatClock.beatToTime(beatIndex + (Number.isFinite(beatOffset) ? beatOffset : 0));
        if (!Number.isFinite(playTime)) return;
        const adjustedTime = Math.max(ctx.currentTime + 0.01, playTime + GUIDE_TIMING_OFFSET_SEC);
        if (adjustedTime <= ctx.currentTime) return;
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(masterGainRef.current || ctx.destination);
        source.start(adjustedTime);
        sourcesRef.current.push(source);
      };

      const scheduleCount = (countKey, beatIndex) => {
        const buffer = countBuffersRef.current.get(normalizeCountKey(countKey));
        if (!buffer) return;
        scheduleBufferAtBeat(buffer, beatIndex);
      };
      const scheduleCountsFrom = (startBeat, includeOne = false) => {
        const countLimit = Math.min(6, beatsPerMeasure);
        if (includeOne && countLimit >= 1) {
          scheduleCount("one", startBeat);
        }
        for (let i = 2; i <= countLimit; i += 1) {
          scheduleCount(COUNT_KEYS[i - 1], startBeat + (i - 1));
        }
      };

      const orderedSections = sections.slice().sort((a, b) => Number(a.start_beat || 0) - Number(b.start_beat || 0));
      const baselineBeat = Number.isFinite(Number(baselineOverride))
        ? Number(baselineOverride)
        : Number.isFinite(Number(startBeat))
          ? Number(startBeat)
          : 0;
      const sectionsToSchedule = orderedSections.filter((section) => Number(section.start_beat || 0) >= baselineBeat);

      const getSectionBeats = (section) => {
        const rawBeats = Number(section?.beats);
        if (Number.isFinite(rawBeats) && rawBeats > 0) {
          return rawBeats;
        }
        const measures = Number(section?.measures || 0);
        return Number.isFinite(measures) && measures > 0 ? measures * beatsPerMeasure : 0;
      };

      if (debugEnabled()) {
        console.log("[guide-debug] schedule", {
          beatsPerMeasure,
          baselineBeat,
          beatOffset,
          anchorAt: beatClock?.anchorAtRef?.current ?? null,
          anchorBeat: beatClock?.anchorBeatRef?.current ?? null,
          sectionCount: sectionsToSchedule.length,
          sections: sectionsToSchedule.map((section) => ({
            id: section.section_id || section.id,
            label: section.label || section.section_label,
            measures: section.measures,
            beats: section.beats,
            startBeat: section.start_beat,
            endBeat: section.end_beat,
            computedBeats: getSectionBeats(section),
          })),
        });
      }

      sectionsToSchedule.forEach((section, index) => {
        const cueKey = getSectionCueKey(section);
        const sectionBeats = getSectionBeats(section);
        const isBeatSection = Number.isFinite(Number(section?.beats)) && Number(section?.beats) > 0;
        const shouldRenderDownbeat = !(isBeatSection && sectionBeats > 0 && sectionBeats < beatsPerMeasure);
        const sectionStartBeat = Number(section.start_beat || 0);
        const cueBuffer = cueKey ? buffersRef.current.get(cueKey) : null;
        if (cueKey) {
          if (cueBuffer) {
            const cueBeat = sectionStartBeat - beatsPerMeasure;
            scheduleBufferAtBeat(cueBuffer, cueBeat);
            scheduleCountsFrom(cueBeat, false);
          }
        }
        if (index === 0 && includeLeadIn) {
          if (beatsPerMeasure === 6) {
            scheduleCount("one", sectionStartBeat - 12);
            scheduleCount("two", sectionStartBeat - 9);
          } else {
            scheduleCount("one", sectionStartBeat - 8);
            scheduleCount("two", sectionStartBeat - 6);
          }
        }

        if (!shouldRenderDownbeat) {
          return;
        }
      });

      const lastSection = orderedSections[orderedSections.length - 1];
      if (lastSection) {
        const lastSectionBeats = getSectionBeats(lastSection);
        const lastIsBeatSection = Number.isFinite(Number(lastSection?.beats)) && Number(lastSection?.beats) > 0;
        const lastFullMeasures = lastIsBeatSection ? Math.floor(lastSectionBeats / beatsPerMeasure) : Number(lastSection.measures || 0);
        if (Number.isFinite(lastFullMeasures) && lastFullMeasures > 0) {
          const lastMeasureStartBeat = Number(lastSection.start_beat || 0) + (lastFullMeasures - 1) * beatsPerMeasure;
          const endingBuffer = buffersRef.current.get("ending");
          if (endingBuffer) {
            scheduleBufferAtBeat(endingBuffer, lastMeasureStartBeat);
          } else {
          }
          scheduleCountsFrom(lastMeasureStartBeat, false);
        }
      }
    },
    [beatClock, beatOffset, ensureBuffers, ensureCtx, includeLeadIn, startBeat, stopAll, timeline],
  );

  React.useEffect(() => {
    startBeatRef.current = startBeat;
    if (!isEnabled) {
      stopAll();
      return;
    }
    const playId = ++playIdRef.current;
    const baseline = startBeatRef.current;
    scheduleCues(baseline).catch((err) => {
      if (playId !== playIdRef.current) return;
    });
  }, [isEnabled, scheduleCues, stopAll, startBeat]);

  React.useEffect(() => stopAll, [stopAll]);

  return {
    stop: stopAll,
  };
}

export default useGuidePlayer;
